import { useState, useMemo } from 'react';
import type { SortDir } from '@/components/ui/sortable-header';

type Getter<T> = (item: T) => string | number | null | undefined;

interface UseClientSortOptions<T> {
  data: T[] | undefined;
  defaultField: string;
  defaultDir?: SortDir;
  fields: Record<string, Getter<T>>;
  pageSize: number;
  filter?: (item: T) => boolean;
}

export function useClientSort<T>({
  data,
  defaultField,
  defaultDir = 'desc',
  fields,
  pageSize,
  filter,
}: UseClientSortOptions<T>) {
  const [sortField, setSortField] = useState(defaultField);
  const [sortDir, setSortDir] = useState<SortDir>(defaultDir);
  const [page, setPage] = useState(1);

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
      setPage(1);
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    return filter ? data.filter(filter) : [...data];
  }, [data, filter]);

  const sorted = useMemo(() => {
    const getter = fields[sortField];
    if (!getter) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      let cmp: number;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortField, sortDir, fields]);

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paged = sorted.slice((page - 1) * pageSize, page * pageSize);

  return {
    sorted,
    paged,
    page,
    setPage,
    totalPages,
    total: sorted.length,
    sortField,
    sortDir,
    toggleSort,
  };
}
